import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Utils
import { getElementId } from '../../../common/elements/utils/elementPropertyUtils';
import { getElementTemplateLink } from '../../../common/templates/templateUtil';
import { getTemplateIsCustom } from './boardTemplatePickerUtil';
import { sendAmplitudeEvent } from '../../analytics/amplitudeService';

// Actions
import { visibleBoardIdSet } from '../../reducers/currentBoardId/currentBoardIdActions';
import { fetchBoard, fetchVisibleDescendantBoards } from '../../element/board/boardService';
import { copyTemplateBoardContents } from './boardTemplateActions';
import { openPopup } from '../../components/popupPanel/popupActions';

// Selectors
import {
    templatesSelector,
    templateCollectionsSelector,
    defaultTemplateListingSelector,
    selectedTemplateBoardSelector,
} from './boardTemplatePickerSelectors';
import { contentTotalCountSelector, contentTotalLimitSelector } from '../../user/elementCount/elementCountSelector';

// Components
import Button from '../../components/buttons/Button';
import BoardTemplatePickerBody from './pickerBody/BoardTemplatePickerBody';
import ClearTemplateToggle from './pickerFooter/ClearTemplateToggle';
import CustomTemplateLoader from './loader/CustomTemplateLoader';

// Constants
import { AMPLITUDE_USER_PROPS, TRACKED_FEATURES } from '../../../common/analytics/statsConstants';
import { ELEMENT_COUNT_LIMIT_POPUP } from '../../user/elementCount/elementCountConstants';

import './BoardTemplatePickerPopup.scss';

const mapStateToProps = createStructuredSelector({
    defaultTemplateListing: defaultTemplateListingSelector,
    templates: templatesSelector,
    templateCollections: templateCollectionsSelector,
    selectedTemplateBoard: selectedTemplateBoardSelector,
    contentCountTotal: contentTotalCountSelector,
    contentCountLimit: contentTotalLimitSelector,
});

const mapDispatchToProps = (dispatch) => ({
    setVisibleBoardId: async (boardId) => {
        if (boardId) {
            dispatch(
                fetchBoard({
                    boardId,
                    addLoadedBoardSummaries: false,
                    loadAncestors: false,
                }),
            ).then(() => dispatch(fetchVisibleDescendantBoards(boardId)));
        }

        dispatch(visibleBoardIdSet({ boardId }));
    },
    copyTemplateContents: (copyTemplateBoardId, currentBoard) =>
        dispatch(copyTemplateBoardContents(copyTemplateBoardId, currentBoard)),
    openBillingAlertPopup: () => dispatch(openPopup(ELEMENT_COUNT_LIMIT_POPUP, () => true)),
});

const BoardTemplatePickerPopup = ({
    selectedTemplateId,
    selectedTemplateBoard,
    currentBoard,
    templates,
    defaultTemplateListing,
    templateCollections,

    keepTemplateContentChecked,
    setKeepTemplateContentChecked,
    dismissTemplatePicker,
    copyTemplateContents,
    setIsCopyingTemplate,
    setSelectedTemplateId,
    setVisibleBoardId,

    onboardingSelectTemplate,
    onboardingHidePicker,

    contentCountTotal,
    contentCountLimit,
    openBillingAlertPopup,
}) => {
    const [navigation, setNavigation] = useState('default');

    const templateForceClear = templates.getIn([selectedTemplateId, 'forceClear']);
    const toggleKeepTemplateDisabled = !selectedTemplateId || templateForceClear;

    const isOverContentLimit = contentCountTotal >= contentCountLimit;

    useEffect(() => {
        // if checkbox for switching keep/clear state of template becomes disabled,
        // uncheck the checkbox
        if (keepTemplateContentChecked && toggleKeepTemplateDisabled) {
            setKeepTemplateContentChecked(false);
        }
    }, [keepTemplateContentChecked, toggleKeepTemplateDisabled]);

    const selectTemplate = async (templateId) => {
        setSelectedTemplateId(templateId);

        const templateBoardId = templates.getIn([templateId, 'templateBoardId']);
        await setVisibleBoardId(templateBoardId || null);

        if (templateId) {
            const isCustomTemplate = getTemplateIsCustom(templates.get(templateId));

            sendAmplitudeEvent({
                eventType: 'Viewed template',
                eventProperties: {
                    templateId: isCustomTemplate ? 'custom' : templateId,
                },
                userProperties: {
                    [AMPLITUDE_USER_PROPS.FEATURE]: { [TRACKED_FEATURES.TEMPLATE_PICKER]: true },
                },
            });
        }
    };

    useEffect(() => {
        if (!onboardingSelectTemplate) return;

        const selectTemplateIdExists = defaultTemplateListing.find((template) => {
            return template?.get('id') === onboardingSelectTemplate;
        });

        if (selectTemplateIdExists) {
            selectTemplate(onboardingSelectTemplate);
        }
    }, [defaultTemplateListing, onboardingSelectTemplate]);

    const copyTemplate = useCallback(async () => {
        if (!selectedTemplateId) {
            return dismissTemplatePicker(getElementId(currentBoard));
        }

        // If the user is over their content limit,
        // dismiss the template and navigate to the account upgrade page
        if (isOverContentLimit) {
            selectTemplate(null);
            dismissTemplatePicker(getElementId(currentBoard));
            return openBillingAlertPopup();
        }

        setIsCopyingTemplate(true);

        const copyTemplateId =
            (keepTemplateContentChecked
                ? getElementId(selectedTemplateBoard)
                : getElementTemplateLink(selectedTemplateBoard)) || selectedTemplateId;

        await copyTemplateContents(copyTemplateId, currentBoard);

        setSelectedTemplateId(null);
        setNavigation('default');
        setIsCopyingTemplate(false);

        if (selectedTemplateId) {
            const isCustomTemplate = getTemplateIsCustom(templates.get(selectedTemplateId));

            sendAmplitudeEvent({
                eventType: 'Used template',
                eventProperties: {
                    templateId: isCustomTemplate ? 'custom' : selectedTemplateId,
                },
                userProperties: {
                    [AMPLITUDE_USER_PROPS.FEATURE]: { [TRACKED_FEATURES.TEMPLATE_PICKER]: true },
                },
            });
        }
    }, [selectedTemplateId, selectedTemplateBoard, currentBoard, keepTemplateContentChecked]);

    const handleCloseButton = () => {
        selectTemplate(null);
        dismissTemplatePicker(getElementId(currentBoard));
    };

    return (
        <div className={classNames('picker-content', { hidden: onboardingHidePicker })}>
            <CustomTemplateLoader />

            <BoardTemplatePickerBody
                selectedTemplateId={selectedTemplateId}
                currentBoard={currentBoard}
                categoriesList={[]}
                templates={templates}
                defaultTemplateListing={defaultTemplateListing}
                templateCollections={templateCollections}
                navigation={navigation}
                setNavigation={setNavigation}
                selectTemplate={selectTemplate}
                handleCloseButton={handleCloseButton}
            />

            <div className="picker-footer">
                <Button className="StyledButton primary use-template-button" onClickFn={copyTemplate}>
                    {selectedTemplateId ? 'Use this template' : 'Continue'}
                </Button>

                <ClearTemplateToggle checked={keepTemplateContentChecked} setChecked={setKeepTemplateContentChecked} />
            </div>
        </div>
    );
};

BoardTemplatePickerPopup.propTypes = {
    selectedTemplateId: PropTypes.string,
    selectedTemplateBoard: PropTypes.object,
    currentBoard: PropTypes.object,
    templates: PropTypes.object,
    defaultTemplateListing: PropTypes.object,
    templateCollections: PropTypes.object,

    contentCountTotal: PropTypes.number,
    contentCountLimit: PropTypes.number,

    onboardingSelectTemplate: PropTypes.string,
    onboardingHidePicker: PropTypes.bool,

    keepTemplateContentChecked: PropTypes.bool,
    setKeepTemplateContentChecked: PropTypes.func,
    setSelectedTemplateId: PropTypes.func,
    setVisibleBoardId: PropTypes.func,
    copyTemplateContents: PropTypes.func,
    dismissTemplatePicker: PropTypes.func,
    setIsCopyingTemplate: PropTypes.func,
    openBillingAlertPopup: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(BoardTemplatePickerPopup);
